import React from 'react';
import { logger } from '../services/logger';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';

const HomePage: React.FC = () => {
  // Log page visit
  React.useEffect(() => {
    logger.info('Home page visited');
  }, []);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-12 animate-fade-in">
        <h1 className="font-display text-5xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-primary">
          Welcome to PhishPhind
        </h1>
        <p
          className="text-xl text-text-secondary animate-fade-in"
          style={{ animationDelay: '200ms' }}
        >
          A multi-step LLM prompting tool for phishing email detection
        </p>
      </div>

      <div
        className="flex justify-center gap-4 mb-16 animate-fade-in"
        style={{ animationDelay: '400ms' }}
      >
        <Button
          variant="primary"
          size="lg"
          hasRipple
          onClick={() => {
            logger.info('Begin analysis clicked');
            window.location.hash = '/analysis';
          }}
        >
          Begin Analysis
        </Button>
        <Button
          variant="glass"
          size="lg"
          hasRipple
          onClick={() => {
            logger.info('View steps clicked');
            window.location.hash = '/steps';
          }}
        >
          View Analysis Steps
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <Card
          variant="glass"
          className="animate-fade-in transform hover:scale-105 transition-all duration-300"
          hasHoverEffect
          style={{ animationDelay: '600ms' }}
        >
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center mb-4 shadow-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-display font-semibold mb-3 text-center">Email Analysis</h2>
            <p className="text-text-secondary text-center">
              Upload or paste email content to begin detailed phishing analysis
            </p>
          </div>
        </Card>

        <Card
          variant="glass"
          className="animate-fade-in transform hover:scale-105 transition-all duration-300"
          hasHoverEffect
          style={{ animationDelay: '800ms' }}
        >
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-gradient-secondary flex items-center justify-center mb-4 shadow-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <h2 className="text-xl font-display font-semibold mb-3 text-center">
              Customizable Steps
            </h2>
            <p className="text-text-secondary text-center">
              Create and modify analysis steps to detect different types of phishing
            </p>
          </div>
        </Card>

        <Card
          variant="glass"
          className="animate-fade-in transform hover:scale-105 transition-all duration-300"
          hasHoverEffect
          style={{ animationDelay: '1000ms' }}
        >
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-gradient-accent flex items-center justify-center mb-4 shadow-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                />
              </svg>
            </div>
            <h2 className="text-xl font-display font-semibold mb-3 text-center">Results Export</h2>
            <p className="text-text-secondary text-center">
              Export analysis results in various formats for documentation
            </p>
          </div>
        </Card>
      </div>

      <div className="mt-16 text-center animate-fade-in" style={{ animationDelay: '1200ms' }}>
        <Button
          variant="outline"
          size="md"
          isGlowing
          onClick={() => {
            logger.info('Documentation clicked');
            // This would link to documentation in a real app
          }}
        >
          View Documentation
        </Button>
      </div>
    </div>
  );
};

export default HomePage;
